import type { MouseEvent } from 'react';
import { usePhoneActions } from '../../hooks/usePhoneActions';
import type { Brand } from '../../lib/telephony';
import { isDialablePhone } from '../../lib/telephony';

interface Props {
  /** The raw phone number. Null/undefined/invalid renders as plain text (or nothing). */
  number: string | null | undefined;
  brand?: Brand;
  customerId?: string;
  customerName?: string;
  /** Where the dial originated, for call attribution. */
  sourceContext?: string;
  /** Override the visible text (e.g. a formatted version). Defaults to `number`. */
  display?: string;
  /** Classes merged onto the clickable element so it matches its surroundings. */
  className?: string;
  /** Stop the click from bubbling to a parent row handler (pick lists / search rows). */
  stopPropagation?: boolean;
}

/**
 * Inline click-to-dial: renders a phone number as text that, when clicked,
 * opens the docked softphone dialpad with the number (and any known brand +
 * customer) prefilled. The user then confirms the brand to call from and
 * places the call — we never dial silently on a single click. This is the
 * primitive for "every phone number in the app is dialable" — drop it in
 * wherever a number was previously plain text or a `tel:` link.
 *
 * Falls back to a plain <span> when the number is missing or not dialable, so
 * it is always a safe drop-in replacement.
 */
export function PhoneNumber({
  number,
  brand,
  customerId,
  customerName,
  sourceContext,
  display,
  className,
  stopPropagation,
}: Props) {
  const { openDialPad } = usePhoneActions();

  if (!number) return null;
  const text = display ?? number;

  if (!isDialablePhone(number)) {
    return <span className={className}>{text}</span>;
  }

  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    if (stopPropagation) e.stopPropagation();
    openDialPad({ number, brand, customerId, customerName, sourceContext });
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      title={`${number} in de dialpad plaatsen`}
      aria-label={customerName ? `Bel ${customerName}` : `Bel ${number}`}
      className={`text-left hover:text-emerald-700 hover:underline underline-offset-2 decoration-dotted cursor-pointer ${className ?? ''}`}
    >
      {text}
    </button>
  );
}
